import React, { useEffect, useState } from 'react';
import { FaLayerGroup, FaThLarge, FaSitemap, FaSyncAlt, FaPlus, FaPen, FaTrash, FaCheck, FaTimes } from 'react-icons/fa';
import ConfirmDeleteDialog from '../../../components/ConfirmDeleteDialog/ConfirmDeleteDialog';
import {
  createUnitCategory,
  createUnitSubcategory,
  createUnitType,
  deleteUnitCategory,
  deleteUnitSubcategory,
  deleteUnitType,
  getUnitCategories,
  getUnitSubcategories,
  getUnitTypes,
  updateUnitCategory,
  updateUnitSubcategory,
  updateUnitType
} from '../../../api/services/adminService';
import './UnitConfig.css';

const initialMessage = { text: '', type: '' };
const DEFAULT_DISPLAY_ORDER = 1;

const EditableListItem = ({
  item,
  onSave,
  onDeleteRequest,
  busyId,
  renderEditExtras
}) => {
  const [isEditing, setIsEditing] = useState(false);
  const [editName, setEditName] = useState('');
  const [editExtras, setEditExtras] = useState({});

  const itemId = item?.id;
  const isBusy = busyId === itemId;

  const startEdit = () => {
    setEditName(item.name || '');
    setEditExtras(renderEditExtras?.getInitial ? renderEditExtras.getInitial(item) : {});
    setIsEditing(true);
  };

  const cancelEdit = () => {
    setIsEditing(false);
    setEditName('');
    setEditExtras({});
  };

  const handleSave = async (e) => {
    e.preventDefault();
    const trimmed = editName.trim();
    if (!trimmed) return;

    const ok = await onSave(item, trimmed, editExtras);
    if (ok) {
      setIsEditing(false);
      setEditName('');
      setEditExtras({});
    }
  };

  return (
    <div className={`uc-item ${isEditing ? 'uc-item-editing' : ''}`}>
      {isEditing ? (
        <form className="uc-item-edit" onSubmit={handleSave}>
          <div className="uc-item-edit-fields">
            <input
              type="text"
              className="uc-input"
              value={editName}
              onChange={(e) => setEditName(e.target.value)}
              disabled={isBusy}
              autoFocus
              required
            />
            {renderEditExtras?.render
              ? renderEditExtras.render({ item, editExtras, setEditExtras, disabled: isBusy })
              : null}
          </div>
          <div className="uc-item-actions">
            <button type="submit" className="uc-icon-btn save" disabled={isBusy || !editName.trim()} title="Save">
              {isBusy ? '…' : <FaCheck />}
            </button>
            <button type="button" className="uc-icon-btn cancel" onClick={cancelEdit} disabled={isBusy} title="Cancel">
              <FaTimes />
            </button>
          </div>
        </form>
      ) : (
        <>
          <div className="uc-item-main">
            <span className="uc-item-name">{item.name}</span>
            {renderEditExtras?.subtitle ? (
              <span className="uc-item-sub">{renderEditExtras.subtitle(item)}</span>
            ) : null}
          </div>
          <div className="uc-item-actions">
            <button
              type="button"
              className="uc-icon-btn edit"
              onClick={startEdit}
              disabled={Boolean(busyId)}
              aria-label={`Edit ${item.name}`}
              title="Edit"
            >
              <FaPen />
            </button>
            <button
              type="button"
              className="uc-icon-btn delete"
              onClick={() => onDeleteRequest(item)}
              disabled={Boolean(busyId)}
              aria-label={`Delete ${item.name}`}
              title="Delete"
            >
              {isBusy ? '…' : <FaTrash />}
            </button>
          </div>
        </>
      )}
    </div>
  );
};

const UnitConfig = () => {
  const [activeTab, setActiveTab] = useState('categories'); // categories | types | subcategories

  const [categories, setCategories] = useState([]);
  const [loadingCategories, setLoadingCategories] = useState(false);
  const [unitTypes, setUnitTypes] = useState([]);
  const [loadingUnitTypes, setLoadingUnitTypes] = useState(false);
  const [subcategories, setSubcategories] = useState([]);
  const [loadingSubcategories, setLoadingSubcategories] = useState(false);
  const [subcategoryCategoryId, setSubcategoryCategoryId] = useState('');

  const [categoryFormData, setCategoryFormData] = useState({ name: '' });
  const [categoryLoading, setCategoryLoading] = useState(false);
  const [categoryMessage, setCategoryMessage] = useState(initialMessage);

  const [unitTypeFormData, setUnitTypeFormData] = useState({ name: '' });
  const [unitTypeLoading, setUnitTypeLoading] = useState(false);
  const [unitTypeMessage, setUnitTypeMessage] = useState(initialMessage);

  const [subcategoryFormData, setSubcategoryFormData] = useState({ name: '', categoryId: '' });
  const [subcategoryLoading, setSubcategoryLoading] = useState(false);
  const [subcategoryMessage, setSubcategoryMessage] = useState(initialMessage);

  const [listBusyId, setListBusyId] = useState(null);
  const [deleteConfirm, setDeleteConfirm] = useState(null);
  const [categoriesListMessage, setCategoriesListMessage] = useState(initialMessage);
  const [typesListMessage, setTypesListMessage] = useState(initialMessage);
  const [subcategoriesListMessage, setSubcategoriesListMessage] = useState(initialMessage);

  useEffect(() => {
    fetchCategories();
    fetchUnitTypes();
  }, []);

  const fetchCategories = async () => {
    try {
      setLoadingCategories(true);
      const response = await getUnitCategories();
      const list = Array.isArray(response?.data) ? response.data : Array.isArray(response) ? response : [];
      setCategories(list);
    } catch (error) {
      console.error('Error fetching categories:', error);
      setCategories([]);
    } finally {
      setLoadingCategories(false);
    }
  };

  const fetchUnitTypes = async () => {
    try {
      setLoadingUnitTypes(true);
      const response = await getUnitTypes();
      const list = Array.isArray(response?.data) ? response.data : Array.isArray(response) ? response : [];
      setUnitTypes(list);
    } catch (error) {
      console.error('Error fetching unit types:', error);
      setUnitTypes([]);
    } finally {
      setLoadingUnitTypes(false);
    }
  };

  const fetchSubcategories = async (categoryId) => {
    if (!categoryId) {
      setSubcategories([]);
      return;
    }

    try {
      setLoadingSubcategories(true);
      const response = await getUnitSubcategories(categoryId);
      const list = Array.isArray(response?.data) ? response.data : Array.isArray(response) ? response : [];
      setSubcategories(list);
    } catch (error) {
      console.error('Error fetching subcategories:', error);
      setSubcategories([]);
    } finally {
      setLoadingSubcategories(false);
    }
  };

  const getErrorMessage = (error, fallback) => error?.message || fallback;
  const isApiSuccess = (response) => !response?.status || response.status === 'success';

  const openDeleteConfirm = (item, itemLabel, deleteHandler) => {
    setDeleteConfirm({ item, itemLabel, deleteHandler });
  };

  const isDeleteInProgress =
    Boolean(deleteConfirm?.item?.id) && listBusyId === deleteConfirm.item.id;

  const closeDeleteConfirm = () => {
    if (isDeleteInProgress) return;
    setDeleteConfirm(null);
  };

  const handleConfirmDelete = async () => {
    if (!deleteConfirm?.item || !deleteConfirm?.deleteHandler) return;
    await deleteConfirm.deleteHandler(deleteConfirm.item);
    setDeleteConfirm(null);
  };

  const handleCategorySubmit = async (e) => {
    e.preventDefault();
    setCategoryMessage(initialMessage);

    if (!categoryFormData.name.trim()) {
      setCategoryMessage({ text: 'Category name is required.', type: 'error' });
      return;
    }

    try {
      setCategoryLoading(true);
      const response = await createUnitCategory(categoryFormData.name.trim(), DEFAULT_DISPLAY_ORDER);
      if (isApiSuccess(response)) {
        setCategoryMessage({ text: response.message || 'Unit category created.', type: 'success' });
        setCategoryFormData({ name: '' });
        await fetchCategories();
      } else {
        setCategoryMessage({ text: response?.message || 'Failed to create unit category.', type: 'error' });
      }
    } catch (error) {
      setCategoryMessage({ text: getErrorMessage(error, 'Failed to create unit category.'), type: 'error' });
    } finally {
      setCategoryLoading(false);
    }
  };

  const handleUnitTypeSubmit = async (e) => {
    e.preventDefault();
    setUnitTypeMessage(initialMessage);

    if (!unitTypeFormData.name.trim()) {
      setUnitTypeMessage({ text: 'Unit type name is required.', type: 'error' });
      return;
    }

    try {
      setUnitTypeLoading(true);
      const response = await createUnitType(unitTypeFormData.name.trim(), DEFAULT_DISPLAY_ORDER);
      if (isApiSuccess(response)) {
        setUnitTypeMessage({ text: response.message || 'Unit type created.', type: 'success' });
        setUnitTypeFormData({ name: '' });
        await fetchUnitTypes();
      } else {
        setUnitTypeMessage({ text: response?.message || 'Failed to create unit type.', type: 'error' });
      }
    } catch (error) {
      setUnitTypeMessage({ text: getErrorMessage(error, 'Failed to create unit type.'), type: 'error' });
    } finally {
      setUnitTypeLoading(false);
    }
  };

  const handleSubcategorySubmit = async (e) => {
    e.preventDefault();
    setSubcategoryMessage(initialMessage);

    if (!subcategoryFormData.name.trim()) {
      setSubcategoryMessage({ text: 'Subcategory name is required.', type: 'error' });
      return;
    }

    if (!subcategoryFormData.categoryId) {
      setSubcategoryMessage({ text: 'Category is required.', type: 'error' });
      return;
    }

    try {
      setSubcategoryLoading(true);
      const response = await createUnitSubcategory(
        subcategoryFormData.name.trim(),
        subcategoryFormData.categoryId,
        DEFAULT_DISPLAY_ORDER
      );

      if (isApiSuccess(response)) {
        setSubcategoryMessage({ text: response.message || 'Unit subcategory created.', type: 'success' });
        // Keep the selected category so the user can keep adding to it.
        setSubcategoryFormData((prev) => ({ name: '', categoryId: prev.categoryId }));
        const viewId = subcategoryCategoryId || subcategoryFormData.categoryId;
        if (viewId) {
          setSubcategoryCategoryId(viewId);
          await fetchSubcategories(viewId);
        }
      } else {
        setSubcategoryMessage({ text: response?.message || 'Failed to create unit subcategory.', type: 'error' });
      }
    } catch (error) {
      setSubcategoryMessage({ text: getErrorMessage(error, 'Failed to create unit subcategory.'), type: 'error' });
    } finally {
      setSubcategoryLoading(false);
    }
  };

  const handleUpdateCategory = async (item, name) => {
    setCategoriesListMessage(initialMessage);
    try {
      setListBusyId(item.id);
      const response = await updateUnitCategory(item.id, name, item.displayOrder ?? DEFAULT_DISPLAY_ORDER);
      if (isApiSuccess(response)) {
        setCategoriesListMessage({ text: response.message || 'Category updated.', type: 'success' });
        await fetchCategories();
        return true;
      }
      setCategoriesListMessage({ text: response?.message || 'Failed to update category.', type: 'error' });
      return false;
    } catch (error) {
      setCategoriesListMessage({ text: getErrorMessage(error, 'Failed to update category.'), type: 'error' });
      return false;
    } finally {
      setListBusyId(null);
    }
  };

  const handleDeleteCategory = async (item) => {
    setCategoriesListMessage(initialMessage);
    try {
      setListBusyId(item.id);
      const response = await deleteUnitCategory(item.id);
      if (isApiSuccess(response)) {
        setCategoriesListMessage({ text: response.message || 'Category deleted.', type: 'success' });
        if (subcategoryCategoryId === String(item.id)) {
          setSubcategoryCategoryId('');
          setSubcategories([]);
        }
        await fetchCategories();
        return;
      }
      setCategoriesListMessage({ text: response?.message || 'Failed to delete category.', type: 'error' });
    } catch (error) {
      setCategoriesListMessage({ text: getErrorMessage(error, 'Failed to delete category.'), type: 'error' });
    } finally {
      setListBusyId(null);
    }
  };

  const handleUpdateUnitType = async (item, name) => {
    setTypesListMessage(initialMessage);
    try {
      setListBusyId(item.id);
      const response = await updateUnitType(item.id, name, item.displayOrder ?? DEFAULT_DISPLAY_ORDER);
      if (isApiSuccess(response)) {
        setTypesListMessage({ text: response.message || 'Unit type updated.', type: 'success' });
        await fetchUnitTypes();
        return true;
      }
      setTypesListMessage({ text: response?.message || 'Failed to update unit type.', type: 'error' });
      return false;
    } catch (error) {
      setTypesListMessage({ text: getErrorMessage(error, 'Failed to update unit type.'), type: 'error' });
      return false;
    } finally {
      setListBusyId(null);
    }
  };

  const handleDeleteUnitType = async (item) => {
    setTypesListMessage(initialMessage);
    try {
      setListBusyId(item.id);
      const response = await deleteUnitType(item.id);
      if (isApiSuccess(response)) {
        setTypesListMessage({ text: response.message || 'Unit type deleted.', type: 'success' });
        await fetchUnitTypes();
        return;
      }
      setTypesListMessage({ text: response?.message || 'Failed to delete unit type.', type: 'error' });
    } catch (error) {
      setTypesListMessage({ text: getErrorMessage(error, 'Failed to delete unit type.'), type: 'error' });
    } finally {
      setListBusyId(null);
    }
  };

  const handleUpdateSubcategory = async (item, name, extras) => {
    setSubcategoriesListMessage(initialMessage);
    const categoryId = extras?.categoryId || item.categoryId || subcategoryCategoryId;
    if (!categoryId) {
      setSubcategoriesListMessage({ text: 'Category is required for subcategory.', type: 'error' });
      return false;
    }

    try {
      setListBusyId(item.id);
      const response = await updateUnitSubcategory(item.id, name, categoryId, item.displayOrder ?? DEFAULT_DISPLAY_ORDER);
      if (isApiSuccess(response)) {
        setSubcategoriesListMessage({ text: response.message || 'Subcategory updated.', type: 'success' });
        await fetchSubcategories(subcategoryCategoryId);
        return true;
      }
      setSubcategoriesListMessage({ text: response?.message || 'Failed to update subcategory.', type: 'error' });
      return false;
    } catch (error) {
      setSubcategoriesListMessage({ text: getErrorMessage(error, 'Failed to update subcategory.'), type: 'error' });
      return false;
    } finally {
      setListBusyId(null);
    }
  };

  const handleDeleteSubcategory = async (item) => {
    setSubcategoriesListMessage(initialMessage);
    try {
      setListBusyId(item.id);
      const response = await deleteUnitSubcategory(item.id);
      if (isApiSuccess(response)) {
        setSubcategoriesListMessage({ text: response.message || 'Subcategory deleted.', type: 'success' });
        await fetchSubcategories(subcategoryCategoryId);
        return;
      }
      setSubcategoriesListMessage({ text: response?.message || 'Failed to delete subcategory.', type: 'error' });
    } catch (error) {
      setSubcategoriesListMessage({ text: getErrorMessage(error, 'Failed to delete subcategory.'), type: 'error' });
    } finally {
      setListBusyId(null);
    }
  };

  const renderMessage = (message) =>
    message.text ? (
      <div className={`uc-alert ${message.type === 'success' ? 'success' : 'error'}`}>
        {message.text}
      </div>
    ) : null;

  const categoryNameById = (id) => categories.find((c) => String(c.id) === String(id))?.name || '—';

  const subcategoryEditExtras = {
    getInitial: (item) => ({ categoryId: item.categoryId || subcategoryCategoryId || '' }),
    subtitle: (item) => categoryNameById(item.categoryId || subcategoryCategoryId),
    render: ({ editExtras, setEditExtras, disabled }) => (
      <select
        className="uc-input uc-select"
        value={editExtras.categoryId || ''}
        onChange={(e) => setEditExtras((prev) => ({ ...prev, categoryId: e.target.value }))}
        disabled={disabled || loadingCategories}
        aria-label="Subcategory category"
      >
        <option value="">Select category</option>
        {categories.map((category) => (
          <option key={category.id} value={category.id}>{category.name}</option>
        ))}
      </select>
    )
  };

  const TABS = [
    { id: 'categories', label: 'Categories', icon: <FaLayerGroup />, count: categories.length },
    { id: 'types', label: 'Unit Types', icon: <FaThLarge />, count: unitTypes.length },
    { id: 'subcategories', label: 'Subcategories', icon: <FaSitemap />, count: subcategories.length },
  ];

  return (
    <div className="uc-container">
      <div className="uc-header">
        <div>
          <h1 className="page-title">Unit Configuration</h1>
          <p className="uc-subtitle">
            Define the building blocks for properties — categories, unit types, and subcategories.
          </p>
        </div>
      </div>

      {/* Summary cards */}
      <div className="uc-summary">
        <div className={`uc-summary-card ${activeTab === 'categories' ? 'is-active' : ''}`} onClick={() => setActiveTab('categories')}>
          <div className="uc-summary-icon cat"><FaLayerGroup /></div>
          <div className="uc-summary-meta">
            <span className="uc-summary-value">{categories.length}</span>
            <span className="uc-summary-label">Categories</span>
          </div>
        </div>
        <div className={`uc-summary-card ${activeTab === 'types' ? 'is-active' : ''}`} onClick={() => setActiveTab('types')}>
          <div className="uc-summary-icon type"><FaThLarge /></div>
          <div className="uc-summary-meta">
            <span className="uc-summary-value">{unitTypes.length}</span>
            <span className="uc-summary-label">Unit Types</span>
          </div>
        </div>
        <div className={`uc-summary-card ${activeTab === 'subcategories' ? 'is-active' : ''}`} onClick={() => setActiveTab('subcategories')}>
          <div className="uc-summary-icon sub"><FaSitemap /></div>
          <div className="uc-summary-meta">
            <span className="uc-summary-value">{subcategoryCategoryId ? subcategories.length : '—'}</span>
            <span className="uc-summary-label">Subcategories</span>
          </div>
        </div>
      </div>

      {/* Tabs */}
      <div className="uc-tabs" role="tablist">
        {TABS.map((tab) => (
          <button
            key={tab.id}
            type="button"
            role="tab"
            aria-selected={activeTab === tab.id}
            className={`uc-tab ${activeTab === tab.id ? 'active' : ''}`}
            onClick={() => setActiveTab(tab.id)}
          >
            <span className="uc-tab-icon">{tab.icon}</span>
            {tab.label}
          </button>
        ))}
      </div>

      {/* Panel: Categories */}
      {activeTab === 'categories' && (
        <div className="uc-panel">
          <div className="uc-panel-head">
            <div>
              <h2 className="uc-panel-title">Unit Categories</h2>
              <p className="uc-panel-desc">Top-level groupings, e.g. Residential, Commercial.</p>
            </div>
            <button type="button" className="uc-refresh" onClick={fetchCategories} disabled={loadingCategories}>
              <FaSyncAlt className={loadingCategories ? 'spin' : ''} /> Refresh
            </button>
          </div>

          <form onSubmit={handleCategorySubmit} className="uc-add-bar">
            <input
              type="text"
              className="uc-input"
              placeholder="New category name (e.g. Residential)"
              value={categoryFormData.name}
              onChange={(e) => setCategoryFormData({ name: e.target.value })}
              disabled={categoryLoading}
            />
            <button type="submit" className="btn btn-primary" disabled={categoryLoading}>
              <FaPlus /> {categoryLoading ? 'Adding…' : 'Add'}
            </button>
          </form>
          {renderMessage(categoryMessage)}
          {renderMessage(categoriesListMessage)}

          <div className="uc-list">
            {loadingCategories ? (
              <div className="uc-state">Loading…</div>
            ) : categories.length === 0 ? (
              <div className="uc-empty">No categories yet. Add your first one above.</div>
            ) : (
              categories.map((c) => (
                <EditableListItem
                  key={c.id || c.name}
                  item={c}
                  busyId={listBusyId}
                  onSave={(item, name) => handleUpdateCategory(item, name)}
                  onDeleteRequest={(item) => openDeleteConfirm(item, 'category', handleDeleteCategory)}
                />
              ))
            )}
          </div>
        </div>
      )}

      {/* Panel: Unit Types */}
      {activeTab === 'types' && (
        <div className="uc-panel">
          <div className="uc-panel-head">
            <div>
              <h2 className="uc-panel-title">Unit Types</h2>
              <p className="uc-panel-desc">Layouts a unit can have, e.g. Studio, 1 Bedroom, Shop.</p>
            </div>
            <button type="button" className="uc-refresh" onClick={fetchUnitTypes} disabled={loadingUnitTypes}>
              <FaSyncAlt className={loadingUnitTypes ? 'spin' : ''} /> Refresh
            </button>
          </div>

          <form onSubmit={handleUnitTypeSubmit} className="uc-add-bar">
            <input
              type="text"
              className="uc-input"
              placeholder="New unit type name (e.g. 1 Bedroom)"
              value={unitTypeFormData.name}
              onChange={(e) => setUnitTypeFormData({ name: e.target.value })}
              disabled={unitTypeLoading}
            />
            <button type="submit" className="btn btn-primary" disabled={unitTypeLoading}>
              <FaPlus /> {unitTypeLoading ? 'Adding…' : 'Add'}
            </button>
          </form>
          {renderMessage(unitTypeMessage)}
          {renderMessage(typesListMessage)}

          <div className="uc-list">
            {loadingUnitTypes ? (
              <div className="uc-state">Loading…</div>
            ) : unitTypes.length === 0 ? (
              <div className="uc-empty">No unit types yet. Add your first one above.</div>
            ) : (
              unitTypes.map((t) => (
                <EditableListItem
                  key={t.id || t.name}
                  item={t}
                  busyId={listBusyId}
                  onSave={(item, name) => handleUpdateUnitType(item, name)}
                  onDeleteRequest={(item) => openDeleteConfirm(item, 'unit type', handleDeleteUnitType)}
                />
              ))
            )}
          </div>
        </div>
      )}

      {/* Panel: Subcategories */}
      {activeTab === 'subcategories' && (
        <div className="uc-panel">
          <div className="uc-panel-head">
            <div>
              <h2 className="uc-panel-title">Unit Subcategories</h2>
              <p className="uc-panel-desc">Nested under a category, e.g. Rental, BnB, Office.</p>
            </div>
          </div>

          {categories.length === 0 ? (
            <div className="uc-empty">Create a category first before adding subcategories.</div>
          ) : (
            <>
              <form onSubmit={handleSubcategorySubmit} className="uc-add-bar uc-add-bar-multi">
                <select
                  className="uc-input uc-select"
                  value={subcategoryFormData.categoryId}
                  onChange={(e) => setSubcategoryFormData((prev) => ({ ...prev, categoryId: e.target.value }))}
                  disabled={subcategoryLoading || loadingCategories}
                >
                  <option value="">Select category…</option>
                  {categories.map((category) => (
                    <option key={category.id} value={category.id}>{category.name}</option>
                  ))}
                </select>
                <input
                  type="text"
                  className="uc-input"
                  placeholder="New subcategory name (e.g. Rental)"
                  value={subcategoryFormData.name}
                  onChange={(e) => setSubcategoryFormData((prev) => ({ ...prev, name: e.target.value }))}
                  disabled={subcategoryLoading}
                />
                <button type="submit" className="btn btn-primary" disabled={subcategoryLoading}>
                  <FaPlus /> {subcategoryLoading ? 'Adding…' : 'Add'}
                </button>
              </form>
              {renderMessage(subcategoryMessage)}

              <div className="uc-view-filter">
                <label htmlFor="view-subcategory-category" className="uc-view-label">Viewing subcategories in</label>
                <select
                  id="view-subcategory-category"
                  className="uc-input uc-select"
                  value={subcategoryCategoryId}
                  onChange={(e) => {
                    const next = e.target.value;
                    setSubcategoryCategoryId(next);
                    setSubcategoriesListMessage(initialMessage);
                    fetchSubcategories(next);
                  }}
                  disabled={loadingCategories || categories.length === 0}
                >
                  <option value="">Select a category to view…</option>
                  {categories.map((c) => (
                    <option key={c.id || c.name} value={c.id}>{c.name}</option>
                  ))}
                </select>
              </div>
              {renderMessage(subcategoriesListMessage)}

              <div className="uc-list">
                {!subcategoryCategoryId ? (
                  <div className="uc-empty">Select a category above to view its subcategories.</div>
                ) : loadingSubcategories ? (
                  <div className="uc-state">Loading…</div>
                ) : subcategories.length === 0 ? (
                  <div className="uc-empty">No subcategories for this category yet.</div>
                ) : (
                  subcategories.map((s) => (
                    <EditableListItem
                      key={s.id || s.name}
                      item={s}
                      busyId={listBusyId}
                      onSave={(item, name, extras) => handleUpdateSubcategory(item, name, extras)}
                      onDeleteRequest={(item) => openDeleteConfirm(item, 'subcategory', handleDeleteSubcategory)}
                      renderEditExtras={subcategoryEditExtras}
                    />
                  ))
                )}
              </div>
            </>
          )}
        </div>
      )}

      <ConfirmDeleteDialog
        open={Boolean(deleteConfirm)}
        itemName={deleteConfirm?.item?.name}
        itemType={deleteConfirm?.itemLabel || 'item'}
        loading={isDeleteInProgress}
        onCancel={closeDeleteConfirm}
        onConfirm={handleConfirmDelete}
      />
    </div>
  );
};

export default UnitConfig;
